import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Divider, FormControl, FormHelperText, Grid, InputLabel,
  ListItemText, MenuItem, OutlinedInput, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import LocationPickerMap from './LocationPickerMap';

// Mirrors net.anipaw.provider.DogServiceType — ImportRowValidator rejects anything else.
const SERVICE_TYPES = [
  { value: 'DOG_WALKING', label: 'Dog walking' },
  { value: 'BOARDING', label: 'Boarding' },
  { value: 'HOUSE_SITTING', label: 'House sitting' },
  { value: 'DROP_IN_VISITS', label: 'Drop-in visits' },
  { value: 'DAY_CARE', label: 'Day care' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'GROOMING', label: 'Grooming' },
  { value: 'STORE', label: 'Store' },
];

const CONSENT_STATUSES = ['GRANTED', 'DECLINED', 'PENDING'];
const CONSENT_SOURCES = ['INSTAGRAM', 'EMAIL', 'PHONE', 'OTHER'];

const EMPTY_FORM = {
  name: '', about: '', city: '', lat: '', lng: '',
  phone: '', website: '', email: '', instagram: '',
  serviceTypes: [], imageUrls: '',
  consentStatus: 'PENDING', consentSource: '', consentGrantedBy: '', consentNote: '',
};

const splitLines = (value) =>
  String(value ?? '').split(/[\n;,]/).map((s) => s.trim()).filter(Boolean);

const trimmedOrNull = (value) => {
  const s = String(value ?? '').trim();
  return s.length > 0 ? s : null;
};

const parseCoordinate = (value) => {
  const s = String(value ?? '').trim();
  if (s.length === 0) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

// The backend's HardenedImageFetcher policy: https only, a real host, no userinfo.
const isPolicyAllowedImageUrl = (raw) => {
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password;
  } catch (e) {
    return false;
  }
};

/**
 * Client-side mirror of ImportRowValidator's field rules, so a typo surfaces immediately
 * instead of after a round trip. The server re-validates regardless — this is convenience,
 * never the authority (duplicate detection in particular is server-only).
 */
export function validateManualForm(form) {
  const errors = {};

  if (!form.name.trim()) errors.name = 'Name is required';

  const lat = parseCoordinate(form.lat);
  const lng = parseCoordinate(form.lng);
  if (lat === null || lng === null) {
    const message = 'Latitude and longitude are required — search or click the map below';
    if (lat === null) errors.lat = message;
    if (lng === null) errors.lng = message;
  } else {
    if (Number.isNaN(lat)) errors.lat = 'Latitude must be a number';
    else if (lat < -90 || lat > 90) errors.lat = 'Latitude must be between -90 and 90';
    if (Number.isNaN(lng)) errors.lng = 'Longitude must be a number';
    else if (lng < -180 || lng > 180) errors.lng = 'Longitude must be between -180 and 180';
  }

  if (form.serviceTypes.length === 0) errors.serviceTypes = 'Pick at least one service type';

  if (!CONSENT_STATUSES.includes(form.consentStatus)) {
    errors.consentStatus = 'Consent status is required';
  }

  if (!form.email.trim() && !form.phone.trim() && !form.instagram.trim()) {
    errors.contact = 'At least one contact channel is required — email, phone, or Instagram';
  }

  const website = form.website.trim();
  if (website && !website.startsWith('https://')) errors.website = 'Website must start with https://';

  const badImages = splitLines(form.imageUrls).filter((url) => !isPolicyAllowedImageUrl(url));
  if (badImages.length > 0) {
    errors.imageUrls = `Rejected by image policy (https URLs only): ${badImages.join(', ')}`;
  }

  return errors;
}

/** Shapes the form into the same ImportRowDto the spreadsheet parser produces. */
export function buildManualRow(form) {
  return {
    rowIndex: 0,
    name: trimmedOrNull(form.name),
    about: trimmedOrNull(form.about),
    city: trimmedOrNull(form.city),
    lat: parseCoordinate(form.lat),
    lng: parseCoordinate(form.lng),
    phone: trimmedOrNull(form.phone),
    website: trimmedOrNull(form.website),
    email: trimmedOrNull(form.email),
    instagram: trimmedOrNull(form.instagram),
    serviceTypes: form.serviceTypes,
    imageUrls: splitLines(form.imageUrls),
    consentStatus: trimmedOrNull(form.consentStatus),
    consentSource: trimmedOrNull(form.consentSource),
    consentGrantedBy: trimmedOrNull(form.consentGrantedBy),
    consentNote: trimmedOrNull(form.consentNote),
  };
}

/**
 * Single-provider entry form. Hands the finished row to `onSubmit`, which pushes it
 * through the same validate/review/commit pipeline the .xlsx path uses — so a manual
 * entry gets identical duplicate detection, image quarantine, and consent recording.
 */
export default function ProviderManualEntryForm({ onSubmit, onCancel, busy }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field] && !prev.contact) return prev;
      const next = { ...prev };
      delete next[field];
      if (['email', 'phone', 'instagram'].includes(field)) delete next.contact;
      return next;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const found = validateManualForm(form);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(buildManualRow(form));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        <Alert severity="info">
          Nothing is created yet. On submit this provider runs through the same duplicate
          and image checks as a spreadsheet import, and you confirm it on the review screen.
        </Alert>

        {errors.contact && <Alert severity="error">{errors.contact}</Alert>}

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Business</Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
            />
            <TextField
              label="About"
              value={form.about}
              onChange={(e) => updateField('about', e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="City"
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
              fullWidth
            />
            <FormControl fullWidth required error={!!errors.serviceTypes}>
              <InputLabel id="manual-service-types-label">Service types</InputLabel>
              <Select
                labelId="manual-service-types-label"
                multiple
                value={form.serviceTypes}
                onChange={(e) => updateField('serviceTypes', e.target.value)}
                input={<OutlinedInput label="Service types" />}
                renderValue={(selected) =>
                  selected
                    .map((v) => (SERVICE_TYPES.find((t) => t.value === v) || {}).label || v)
                    .join(', ')
                }
              >
                {SERVICE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Checkbox checked={form.serviceTypes.includes(type.value)} />
                    <ListItemText primary={type.label} />
                  </MenuItem>
                ))}
              </Select>
              {errors.serviceTypes && <FormHelperText>{errors.serviceTypes}</FormHelperText>}
            </FormControl>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Location</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Latitude"
                type="number"
                value={form.lat}
                onChange={(e) => updateField('lat', e.target.value)}
                error={!!errors.lat}
                helperText={errors.lat}
                inputProps={{ step: 'any' }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Longitude"
                type="number"
                value={form.lng}
                onChange={(e) => updateField('lng', e.target.value)}
                error={!!errors.lng}
                helperText={errors.lng}
                inputProps={{ step: 'any' }}
                fullWidth
                required
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <LocationPickerMap
              latitude={form.lat}
              longitude={form.lng}
              onLocationChange={(lat, lng) => {
                setForm((prev) => ({
                  ...prev,
                  lat: lat.toFixed(6),
                  lng: lng.toFixed(6),
                }));
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.lat;
                  delete next.lng;
                  return next;
                });
              }}
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Contact</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            At least one of email, phone, or Instagram is required — it becomes the identifier
            used for duplicate detection and for sending the claim invite.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                error={!!errors.contact}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                error={!!errors.contact}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Instagram"
                value={form.instagram}
                onChange={(e) => updateField('instagram', e.target.value)}
                error={!!errors.contact}
                placeholder="@handle"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Website"
                value={form.website}
                onChange={(e) => updateField('website', e.target.value)}
                error={!!errors.website}
                helperText={errors.website || 'Must start with https://'}
                placeholder="https://"
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Images</Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Image URLs"
            value={form.imageUrls}
            onChange={(e) => updateField('imageUrls', e.target.value)}
            error={!!errors.imageUrls}
            helperText={
              errors.imageUrls ||
              'One https URL per line. Each is fetched into quarantine, moderated, and only then published.'
            }
            fullWidth
            multiline
            minRows={3}
            placeholder={'https://example.com/photo-1.jpg\nhttps://example.com/photo-2.jpg'}
          />
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Consent</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.consentStatus}>
                <InputLabel id="manual-consent-status-label">Consent status</InputLabel>
                <Select
                  labelId="manual-consent-status-label"
                  value={form.consentStatus}
                  label="Consent status"
                  onChange={(e) => updateField('consentStatus', e.target.value)}
                >
                  {CONSENT_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
                {errors.consentStatus && <FormHelperText>{errors.consentStatus}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="manual-consent-source-label">Consent source</InputLabel>
                <Select
                  labelId="manual-consent-source-label"
                  value={form.consentSource}
                  label="Consent source"
                  onChange={(e) => updateField('consentSource', e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {CONSENT_SOURCES.map((source) => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Consent granted by"
                value={form.consentGrantedBy}
                onChange={(e) => updateField('consentGrantedBy', e.target.value)}
                helperText="Who at the business agreed, as recorded in the outreach thread"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Consent note"
                value={form.consentNote}
                onChange={(e) => updateField('consentNote', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? 'Validating…' : 'Validate provider'}
          </Button>
          <Button color="inherit" onClick={onCancel} disabled={busy}>Cancel</Button>
        </Stack>
      </Stack>
    </Box>
  );
}
